import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall } from "utils/service";

// The MCP OAuth login/consent bridge. See:
// ss-core-service-mcp's Inkfree Authorization Server delegates its
// login/consent screens to these two pages rather than rendering its own, so
// normal Inkfree login rules (MFA, lockout, password expiry) and branding
// apply. Mirrors Mailzzy's own McpAuthorize/OAuthConsent (ss-mailzzy-web) --
// the bridge page does NOT duplicate the login form; it reuses the app's
// existing Login/OTP/SetupMFA screens via a `redirectionUrl` bounce-back
// (see Login.tsx/OTP.tsx/SetupMFA.tsx/authSession.ts) and only takes over
// once the user is already authenticated in this SPA.

export interface McpConsentClientInfo {
  clientName: string;
  scopes: string[];
}

/**
 * Friendly display name for the DCR client_id shown on the consent screen.
 * Authenticated (the user is already logged in by the time consent renders).
 * Unknown client_id -> empty name; the page falls back to a generic label.
 */
export async function getMcpConsentClientInfo(
  clientId: string,
): Promise<McpConsentClientInfo> {
  const url = `${urls.GET_MCP_CONSENT_CLIENT_INFO}?client_id=${encodeURIComponent(clientId)}`;
  const callParams = await getCallParams("GET");
  return await makeCall(url, callParams);
}

/**
 * Builds a hidden form and submits it as a real top-level navigation — NOT
 * fetch/XHR. `/mcp/auth-bridge` and `/oauth2/authorize` both set a
 * `Set-Cookie` and 302-redirect the browser onward; a fetch call would
 * receive that redirect as an opaque response and go nowhere. Values are set
 * via `input.value` (a DOM property), never string-concatenated into the URL
 * or pre-encoded — the browser form-encodes them correctly on submit.
 */
const submitForm = (action: string, fields: Array<[string, string]>) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  form.style.display = "none";
  fields.forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

export const submitMcpAuthBridge = (authToken: string): void => {
  submitForm(urls.MCP_AUTH_BRIDGE, [["token", authToken]]);
};

/**
 * Spring Authorization Server's default consent-approval contract expects
 * one `scope` form field per approved scope, not a single joined string —
 * hence the array of `["scope", value]` tuples rather than a single field.
 */
export const submitMcpOAuthConsent = (
  clientId: string,
  state: string,
  approvedScopes: string[],
): void => {
  const fields: Array<[string, string]> = [
    ["client_id", clientId],
    ["state", state],
    ...approvedScopes.map((scope): [string, string] => ["scope", scope]),
  ];
  submitForm(urls.MCP_OAUTH_AUTHORIZE, fields);
};
